import React, {useMemo, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import {DataGridPro} from "@mui/x-data-grid-pro";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

/**
 * Game-agnostic mass charge flow: List → Items → Check → Result.
 *
 * Props (everything game-specific is injected):
 *   adapter          — how to read/write one charge item ("entry") of the game's constructor, see createGuidMapAdapter
 *   templates        — charge templates of the game ({title, data}); data is a JSON list of entries
 *   buildChargeData  — (entries) => payload of the game's charge endpoint; also used to group users with equal charges
 *   checkExist       — (ids) => Promise<{existent, nonExistent}>
 *   createCharge     — ({userIds, chargeData}) => Promise
 *   renderItemEditor — (entry, onChange) => JSX — the game's charge constructor in single-item mode
 */

const STEPS = ["List", "Items", "Check", "Result"];
const HEADER_TOKENS = new Set(["userid", "user_id", "id", "stateid", "state_id", "deviceid", "device_id"]);
const EMPTY_SELECTION = {type: "include", ids: new Set()};

// Delimiter: tab, ';', ',' or whitespace.
//   "userId"                  → just the user
//   "userId templateName"     → 2 columns: the second one is a charge template
//   "userId itemName amount"  → 3+ columns: item (may contain spaces) and a trailing numeric amount
function parseLine(line) {
    const delimiter = line.includes("\t") ? "\t" : line.includes(";") ? ";" : line.includes(",") ? "," : /\s+/;
    const tokens = line.split(delimiter).map(t => t.trim()).filter(Boolean);
    if (tokens.length === 0) return null;
    const [userId, ...rest] = tokens;
    if (rest.length === 1) return {userId, itemName: "", amount: null, template: rest[0]};
    let amount = null;
    if (rest.length > 0 && /^-?\d+(\.\d+)?$/.test(rest[rest.length - 1])) {
        amount = Number(rest.pop());
    }
    return {userId, itemName: rest.join(" "), amount, template: ""};
}

function parseList(text) {
    return text
        .split(/\r?\n/)
        .map(l => l.trim())
        .filter(Boolean)
        .map(parseLine)
        .filter(Boolean)
        .filter((row, i) => !(i === 0 && HEADER_TOKENS.has(row.userId.toLowerCase())));
}

/**
 * Adapter for the guidMap-based constructor (toh/mc/ds Guid2Block).
 * Entry shape: {item: chargeType, guid?, guid2?, amount?, ...}
 */
export function createGuidMapAdapter(chargeTypes, guidMap) {
    const hasItem = (entry) => Boolean(entry?.item?.value);
    return {
        newEntry: () => ({item: {value: "", guidMap: "", guidMap2: ""}}),
        hasItem,
        label: (entry) => {
            if (!hasItem(entry)) return "";
            const parts = [entry.item.name || entry.item.value];
            if (entry.guid?.displayName) parts.push(entry.guid.displayName);
            if (entry.guid2?.displayName) parts.push(entry.guid2.displayName);
            return parts.join(": ");
        },
        id: (entry) => entry?.guid?.guid || entry?.guid2?.guid || "",
        getAmount: (entry) => entry?.amount ?? null,
        setAmount: (entry, amount) => ({...entry, amount}),
        // itemName → entry. Charge type name first, then guidMap displayName/guid (first charge type with that guidMap).
        resolve: (itemName) => {
            if (!itemName || !chargeTypes) return null;
            const needle = itemName.toLowerCase();

            const byType = chargeTypes.find(ct => ct.name?.toLowerCase() === needle || ct.value?.toLowerCase() === needle);
            if (byType && !byType.guidMap) return {item: byType};

            for (const [category, options] of Object.entries(guidMap || {})) {
                const option = options.find(o => o.displayName?.toLowerCase() === needle || o.guid?.toLowerCase() === needle);
                if (!option) continue;
                const chargeType = chargeTypes.find(ct => ct.guidMap === category);
                if (chargeType) return {item: chargeType, guid: option};
            }
            return byType ? {item: byType} : null;
        }
    };
}

export default function MassChargeWizard({
    adapter,
    templates = [],
    buildChargeData,
    checkExist,
    createCharge,
    renderItemEditor
}) {
    const [step, setStep] = useState(0);
    const [text, setText] = useState("");
    const [rows, setRows] = useState([]);
    const [selection, setSelection] = useState(EMPTY_SELECTION);
    const [itemDialog, setItemDialog] = useState(null); // {rowId, entry}
    const [clipboard, setClipboard] = useState(null);   // {entry, amount, template, itemName} copied with Ctrl+C
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [results, setResults] = useState([]);

    const selectedIds = selection.ids;
    const templateByTitle = useMemo(() => Object.fromEntries(templates.map(t => [t.title, t])), [templates]);

    // ---------- row helpers ----------

    const rowAmount = (row) => adapter.getAmount(row.entry) ?? row.amount ?? null;

    const rowProblem = (row) => {
        if (row.exists === false) return "User not found";
        if (row.template) return templateByTitle[row.template] ? null : "Template not found";
        return adapter.hasItem(row.entry) ? null : "No item or template";
    };

    const chargeDataFor = (row) => {
        if (row.template) return buildChargeData(JSON.parse(templateByTitle[row.template].data));
        return buildChargeData([row.entry]);
    };

    const targetsFor = (rowId) => (selectedIds.has(rowId) ? selectedIds : new Set([rowId]));

    const applyToRows = (targets, patch) => {
        setRows(prev => prev.map(r => (targets.has(r.id) ? patch(r) : r)));
    };

    // ---------- step 0: list ----------

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => setText(e.target.result);
        reader.readAsText(file);
        event.target.value = "";
    };

    const buildRows = () => {
        const parsed = parseList(text).map((p, i) => {
            // template from the list: match by title (case-insensitive); unknown names go to Item as "not found"
            const template = p.template
                ? templates.find(t => t.title.toLowerCase() === p.template.toLowerCase())
                : null;
            const itemName = p.template && !template ? p.template : p.itemName;
            let entry = adapter.resolve(itemName);
            if (entry && p.amount !== null) entry = adapter.setAmount(entry, p.amount);
            return {
                id: i + 1,
                userId: p.userId,
                itemName,
                amount: p.amount,
                entry,
                template: template ? template.title : "",
                exists: undefined
            };
        });
        setRows(parsed);
        setSelection(EMPTY_SELECTION);
        setResults([]);
        setError(null);
        setStep(1);
    };

    // ---------- step 1: items ----------

    // Editing a cell of a selected row applies the change to every selected row (userId excluded).
    const processRowUpdate = (newRow, oldRow) => {
        const patch = {};
        if (newRow.amount !== oldRow.amount) patch.amount = newRow.amount === "" ? null : newRow.amount;
        if (newRow.template !== oldRow.template) patch.template = newRow.template || "";

        const apply = (r) => {
            const next = {...r, ...patch};
            if ("amount" in patch && next.entry) next.entry = adapter.setAmount(next.entry, patch.amount);
            return next;
        };

        const targets = Object.keys(patch).length > 0 ? targetsFor(oldRow.id) : new Set([oldRow.id]);
        const updated = apply({...oldRow, userId: newRow.userId});
        setRows(prev => prev.map(r => (r.id === oldRow.id ? updated : targets.has(r.id) ? apply(r) : r)));
        return updated;
    };

    const openItemDialog = (row) => {
        const entry = adapter.hasItem(row.entry)
            ? row.entry
            : adapter.setAmount(adapter.newEntry(), row.amount ?? undefined);
        setItemDialog({rowId: row.id, entry});
    };

    const saveItemDialog = () => {
        const {rowId, entry} = itemDialog;
        const next = adapter.hasItem(entry) ? entry : null;
        applyToRows(targetsFor(rowId), (r) => ({
            ...r,
            entry: next
                ? (adapter.getAmount(next) == null && r.amount != null ? adapter.setAmount(next, r.amount) : next)
                : null,
            itemName: next ? adapter.label(next) : r.itemName
        }));
        setItemDialog(null);
    };

    // Ctrl/Cmd+C copies the row's charge config, Ctrl/Cmd+V pastes it into every selected row (or the focused one)
    const handleCellKeyDown = (params, event) => {
        if (!editable || !(event.ctrlKey || event.metaKey)) return;
        const key = event.key.toLowerCase();
        if (key === "c") {
            const {entry, amount, template, itemName} = params.row;
            setClipboard({entry, amount, template, itemName});
        } else if (key === "v" && clipboard) {
            event.preventDefault();
            const targets = selectedIds.size > 0 ? selectedIds : new Set([params.row.id]);
            applyToRows(targets, (r) => ({...r, ...clipboard}));
        }
    };

    const removeSelected = () => {
        setRows(prev => prev.filter(r => !selectedIds.has(r.id)));
        setSelection(EMPTY_SELECTION);
    };

    const validate = async () => {
        setBusy(true);
        setError(null);
        try {
            const ids = [...new Set(rows.map(r => r.userId).filter(Boolean))];
            const response = await checkExist(ids);
            const existent = new Set(response?.existent ?? []);
            setRows(prev => prev.map(r => ({...r, exists: existent.has(r.userId)})));
            setSelection(EMPTY_SELECTION);
            setStep(2);
        } catch (e) {
            setError(e?.data?.message || e?.error || "Failed to validate ids");
        } finally {
            setBusy(false);
        }
    };

    // ---------- step 2 → 3: charge ----------

    const validRows = rows.filter(r => rowProblem(r) === null);
    const skippedRows = rows.filter(r => rowProblem(r) !== null);

    const charge = async () => {
        // one request per distinct charge payload; invalid rows are skipped
        const groups = new Map();
        validRows.forEach(r => {
            const chargeData = chargeDataFor(r);
            const key = JSON.stringify(chargeData);
            if (!groups.has(key)) {
                groups.set(key, {
                    key,
                    label: r.template ? `Template: ${r.template}` : adapter.label(r.entry),
                    chargeData,
                    userIds: []
                });
            }
            const group = groups.get(key);
            if (!group.userIds.includes(r.userId)) group.userIds.push(r.userId);
        });

        setStep(3);
        setBusy(true);
        setResults([]);
        for (const group of groups.values()) {
            try {
                const data = await createCharge({userIds: group.userIds, chargeData: group.chargeData});
                setResults(prev => [...prev, {...group, status: "ok", data}]);
            } catch (e) {
                setResults(prev => [...prev, {...group, status: "error", error: e?.data?.message || e?.error || JSON.stringify(e)}]);
            }
        }
        setBusy(false);
    };

    const reset = () => {
        setStep(0);
        setRows([]);
        setResults([]);
        setSelection(EMPTY_SELECTION);
        setError(null);
    };

    // ---------- grid ----------

    const editable = step === 1;

    const columns = useMemo(() => {
        const cols = [
            {field: "userId", headerName: "User ID", flex: 1.4, minWidth: 220, editable},
            {
                field: "itemName",
                headerName: "Item",
                flex: 1.4,
                minWidth: 220,
                sortable: false,
                renderCell: (params) => (
                    <Stack direction="row" spacing={1} sx={{alignItems: "center", width: "100%", opacity: params.row.template ? 0.5 : 1}}>
                        <Typography variant="body2" noWrap sx={{flexGrow: 1}}>
                            {adapter.hasItem(params.row.entry)
                                ? adapter.label(params.row.entry)
                                : params.row.itemName
                                    ? <Chip size="small" color="warning" variant="outlined" label={`${params.row.itemName} — not found`}/>
                                    : <Typography component="span" variant="body2" color="text.disabled">—</Typography>}
                        </Typography>
                        {editable && (
                            <Tooltip title={selectedIds.has(params.row.id) && selectedIds.size > 1 ? `Set item for ${selectedIds.size} selected rows` : "Set item"}>
                                <IconButton size="small" aria-label="edit item" onClick={() => openItemDialog(params.row)}>
                                    <EditIcon fontSize="inherit"/>
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                )
            },
            {field: "itemId", headerName: "Item ID", flex: 1, minWidth: 160, valueGetter: (value, row) => adapter.id(row.entry)},
            {
                field: "amount",
                headerName: "Amount",
                type: "number",
                width: 110,
                editable,
                valueGetter: (value, row) => rowAmount(row)
            },
            {
                field: "template",
                headerName: "Template",
                flex: 1,
                minWidth: 160,
                type: "singleSelect",
                editable,
                valueOptions: [{value: "", label: "—"}, ...templates.map(t => ({value: t.title, label: t.title}))]
            }
        ];
        if (step >= 2) {
            cols.push({
                field: "status",
                headerName: "Status",
                width: 170,
                sortable: false,
                valueGetter: (value, row) => rowProblem(row) ?? "OK",
                renderCell: (params) => (
                    <Chip size="small" variant="outlined"
                          color={params.value === "OK" ? "success" : "error"}
                          label={params.value}/>
                )
            });
        }
        return cols;
    }, [adapter, templates, editable, step, selectedIds, rows.length]); // eslint-disable-line react-hooks/exhaustive-deps

    const grid = (
        <Box sx={{height: 480, width: "100%"}}>
            <DataGridPro
                rows={rows}
                columns={columns}
                checkboxSelection={editable}
                disableRowSelectionOnClick
                disableRowSelectionExcludeModel
                rowSelectionModel={selection}
                onRowSelectionModelChange={setSelection}
                isCellEditable={(params) => editable && !(params.field === "amount" && params.row.template)}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={(e) => setError(String(e))}
                onCellKeyDown={handleCellKeyDown}
                getRowClassName={(params) => (step >= 2 && rowProblem(params.row) ? "mass-charge-row--invalid" : "")}
                sx={{
                    "& .mass-charge-row--invalid": {
                        bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
                        "&:hover": {bgcolor: (theme) => alpha(theme.palette.error.main, 0.2)}
                    }
                }}
            />
        </Box>
    );

    // ---------- render ----------

    return (
        <Stack spacing={2}>
            <Stepper activeStep={step} sx={{pt: 1}}>
                {STEPS.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
            </Stepper>

            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

            {step === 0 && (
                <>
                    <Alert severity="info">
                        One user per line, columns separated by tab, comma, semicolon or space:
                        <b> userId</b> — just the user; <b>userId template</b> — two columns, the second is a charge template;
                        <b> userId itemName amount</b> — three or more columns. Items and templates can also be set on the next step.
                    </Alert>
                    <TextField
                        label="List of user IDs"
                        placeholder={"3a78896e879a67d889ac3ed2271159ca\n3a78896e879a67d889ac3ed2271159ca Welcome pack\n3a78896e879a67d889ac3ed2271159ca Gold 100"}
                        multiline
                        rows={8}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                    />
                    <Stack direction="row" spacing={2} sx={{alignItems: "center"}}>
                        <Button variant="outlined" component="label">
                            Select file .txt or .csv
                            <input accept=".txt,.csv" type="file" hidden onChange={handleFileUpload}/>
                        </Button>
                        <Box sx={{flexGrow: 1}}/>
                        <Button variant="contained" disabled={text.trim() === ""} onClick={buildRows}>Next</Button>
                    </Stack>
                </>
            )}

            {step === 1 && (
                <>
                    <Stack direction="row" spacing={2} sx={{alignItems: "center"}}>
                        <Typography variant="body2" color="text.secondary">
                            {rows.length} rows{selectedIds.size > 0 ? `, ${selectedIds.size} selected` : ""}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{flexGrow: 1}}>
                            {clipboard
                                ? <>Copied <b>{clipboard.template ? `Template: ${clipboard.template}` : adapter.label(clipboard.entry) || "empty item"}</b>.
                                    Select rows and press Ctrl+V to paste Item, Amount and Template into them.</>
                                : <>Select rows and edit one of them to apply Item, Amount or Template to all selected rows.
                                    Ctrl+C on a row copies its Item, Amount and Template; Ctrl+V pastes into selected rows.</>}
                        </Typography>
                        <Button variant="outlined" color="error" startIcon={<DeleteIcon/>}
                                disabled={selectedIds.size === 0} onClick={removeSelected}>
                            Remove selected
                        </Button>
                    </Stack>
                    {grid}
                    <Stack direction="row" spacing={2}>
                        <Button variant="outlined" onClick={() => setStep(0)}>Back</Button>
                        <Box sx={{flexGrow: 1}}/>
                        <Button variant="contained" loading={busy} disabled={rows.length === 0} onClick={validate}>Next</Button>
                    </Stack>
                </>
            )}

            {step === 2 && (
                <>
                    <Alert severity={skippedRows.length > 0 ? "warning" : "success"}>
                        {validRows.length} of {rows.length} rows will be charged
                        {skippedRows.length > 0 && <>, <b>{skippedRows.length} highlighted rows will be skipped</b></>}.
                    </Alert>
                    {grid}
                    <Stack direction="row" spacing={2}>
                        <Button variant="outlined" onClick={() => setStep(1)}>Back</Button>
                        <Box sx={{flexGrow: 1}}/>
                        <Button variant="contained" color="success" disabled={validRows.length === 0} onClick={charge}>
                            Charge {validRows.length} of {rows.length}
                        </Button>
                    </Stack>
                </>
            )}

            {step === 3 && (
                <>
                    {busy && <LinearProgress/>}
                    {results.map(group => (
                        <Alert key={group.key} severity={group.status === "ok" ? "success" : "error"}>
                            <b>{group.label}</b> — {group.userIds.length} users{group.status === "error" && <>: {group.error}</>}
                            <Typography variant="caption" component="div" sx={{mt: 0.5, wordBreak: "break-all"}}>
                                {group.userIds.join(", ")}
                            </Typography>
                        </Alert>
                    ))}
                    {skippedRows.length > 0 && (
                        <Alert severity="warning">
                            <b>Skipped {skippedRows.length} rows</b>
                            {skippedRows.map(r => (
                                <Typography key={r.id} variant="caption" component="div">{r.userId} — {rowProblem(r)}</Typography>
                            ))}
                        </Alert>
                    )}
                    <Stack direction="row" spacing={2}>
                        <Button variant="outlined" disabled={busy} onClick={() => setStep(2)}>Back</Button>
                        <Box sx={{flexGrow: 1}}/>
                        <Button variant="contained" disabled={busy} onClick={reset}>New mass charge</Button>
                    </Stack>
                </>
            )}

            {itemDialog && (
                <Dialog open fullWidth maxWidth="md" onClose={() => setItemDialog(null)}>
                    <DialogTitle>
                        Charge item
                        {selectedIds.has(itemDialog.rowId) && selectedIds.size > 1 && (
                            <Typography variant="body2" color="text.secondary">
                                Will be applied to {selectedIds.size} selected rows. Use a template to charge several items at once.
                            </Typography>
                        )}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{pt: 1}}>
                            {renderItemEditor(itemDialog.entry, (entry) => setItemDialog(d => ({...d, entry})))}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" color="error" onClick={() => setItemDialog(null)}>Cancel</Button>
                        <Button variant="outlined" onClick={() => setItemDialog(d => ({...d, entry: adapter.newEntry()}))}>Clear</Button>
                        <Button variant="contained" onClick={saveItemDialog}>Apply</Button>
                    </DialogActions>
                </Dialog>
            )}
        </Stack>
    );
}
